/**
 * Slug generator for the announcement form (#1946).
 *
 * THE SLUG IS AN IDEMPOTENCY KEY THAT LOOKS LIKE A NAME. Re-using one does
 * not create a second announcement — it rewrites whatever that slug already
 * said, for every tenant, and re-delivers it. A hand-typed `new-model` is one
 * keystroke away from overwriting last month's `new-model`, and nothing in the
 * form can warn about it: the collision is only visible afterwards, as
 * `resurfaced > 0` in the result panel.
 *
 * So the generated shape is timestamp-first with a random tail: readable
 * enough to recognise in the audit log, and drawn locally rather than reserved
 * with the server.
 *
 * COLLISION IS UNLIKELY, NOT IMPOSSIBLE, and nothing downstream checks. The
 * minute stamp already separates sends more than a minute apart; within one
 * minute two draws collide with probability 1/36^4 ≈ 1 in 1.7 million, and
 * neither this module nor the platform route looks up whether the slug exists
 * — a collision would present as `resurfaced > 0` in the result panel, after
 * the fact. That is the same evidence a hand-typed collision leaves; the
 * generator makes it rare, it does not make it detectable.
 *
 * Local time, not UTC — the timestamp is a label for the person writing the
 * copy, and every operator here reads Shanghai time.
 */

const SUFFIX_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const SUFFIX_LENGTH = 4;

function pad(value: number, width: number): string {
  return value.toString().padStart(width, '0');
}

/**
 * Largest multiple of the alphabet size that fits in a byte (7 × 36). Bytes at
 * or above it are discarded rather than folded: 256 is not a multiple of 36,
 * so a plain `byte % 36` would draw the first four symbols of the alphabet
 * 8/256 of the time against 7/256 for the rest — ~14% over-represented. The
 * whole job of this tail is to be an unbiased draw, and rejection costs a
 * redraw about 1.6% of the time.
 */
const SUFFIX_REJECT_AT = 252;

function randomSuffix(): string {
  let suffix = '';
  while (suffix.length < SUFFIX_LENGTH) {
    const bytes = new Uint8Array(SUFFIX_LENGTH);
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte >= SUFFIX_REJECT_AT) continue;
      suffix += SUFFIX_ALPHABET[byte % SUFFIX_ALPHABET.length];
      if (suffix.length === SUFFIX_LENGTH) break;
    }
  }
  return suffix;
}

/**
 * `YYYYMMDD-HHmm-xxxx`, e.g. `20260816-1802-k3f9`. Always matches
 * `NOTIFICATION_ANNOUNCE_SLUG_PATTERN`: it opens with a digit, carries only
 * digits, lowercase letters and dashes, and is 18 characters long.
 */
export function generateAnnouncementSlug(now: Date = new Date()): string {
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1, 2)}${pad(now.getDate(), 2)}` +
    `-${pad(now.getHours(), 2)}${pad(now.getMinutes(), 2)}`;
  return `${stamp}-${randomSuffix()}`;
}
